import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Chip,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  RadioGroup,
  Radio,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  CircularProgress,
  Alert
} from '@mui/material';
import api from '../../services/api';
import { classificationApi, envisDevicesApi } from '../../services/api';
import { FINDINGS_FETCH_LIMIT } from '../../hooks/useReportData';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ReportPreviewModal from './ReportPreviewModal';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import SummarizeIcon from '@mui/icons-material/Summarize';
import MapIcon from '@mui/icons-material/Map';
import DescriptionIcon from '@mui/icons-material/Description';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import DirectionsIcon from '@mui/icons-material/Directions';
import BugReportIcon from '@mui/icons-material/BugReport';
import MemoryIcon from '@mui/icons-material/Memory';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FilterListIcon from '@mui/icons-material/FilterList';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import DisciplineAccessControl from '../common/DisciplineAccessControl';

// Defect types will be fetched from API

// Mock data is no longer needed - using real API data instead

// Reports will be fetched from backend via /api/reports

// Format options
const formatOptions = ['PDF', 'XLSX', 'CSV'];

// What the report is built from. 'validated' findings are the confirmed defects
// shown everywhere else in the app; 'draft' are AI detections nobody has reviewed.
const REPORT_CONTENT_OPTIONS = [
  {
    value: 'validated',
    label: 'Validated defects',
    description: 'Confirmed defects that a reviewer has validated',
    noun: 'validated defects'
  },
  {
    value: 'draft',
    label: 'Findings pending validation',
    description: 'AI detections not yet reviewed - types and locations are unconfirmed',
    noun: 'pending findings'
  }
];

const getReportContentOption = (value) =>
  REPORT_CONTENT_OPTIONS.find(option => option.value === value) || REPORT_CONTENT_OPTIONS[0];

const ReportsPage = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('');
  const [openGenerateDialog, setOpenGenerateDialog] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [previewFilters, setPreviewFilters] = useState(null);
  
  // ENViS Report specific states
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [selectedSections, setSelectedSections] = useState('all'); // 'all' or 'specific'
  const [specificSections, setSpecificSections] = useState([]);
  const [selectedDefectTypes, setSelectedDefectTypes] = useState([]);
  const [selectAllDefects, setSelectAllDefects] = useState(false);
  const [hasChosenDefectTypes, setHasChosenDefectTypes] = useState(false);
  const [includeRepeatedFindings, setIncludeRepeatedFindings] = useState(false);
  const [onlyDefectsWithRepeated, setOnlyDefectsWithRepeated] = useState(false);
  const [selectAllVehicles, setSelectAllVehicles] = useState(true);
  const [selectedVehicleUuids, setSelectedVehicleUuids] = useState([]);
  // 'validated' = confirmed defects (default), 'draft' = AI findings awaiting validation
  const [findingStatus, setFindingStatus] = useState('validated');

  // Stats state for dashboard cards
  const [stats, setStats] = useState({
    activeDevices: 0,
    aiDetections: 0,
    networks: 0,
    reportsGenerated: 0,
    loading: true
  });

  // Real networks and sections data
  const [networksData, setNetworksData] = useState([]);
  const [sectionsData, setSectionsData] = useState([]);
  const [loadingNetworks, setLoadingNetworks] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);

  // Vehicle sources are organization-wide. Devices are not restricted by the
  // selected network because findings are matched to networks independently.
  const [vehiclesData, setVehiclesData] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [vehiclesError, setVehiclesError] = useState(null);

  // Real defect types data
  const [defectTypes, setDefectTypes] = useState([]);
  const [loadingDefectTypes, setLoadingDefectTypes] = useState(false);

  // Validated defects count for summary
  const [validatedDefectsCount, setValidatedDefectsCount] = useState(null);
  const [loadingDefectsCount, setLoadingDefectsCount] = useState(false);

  // Reports list state
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);

  // Fetch reports from backend
  const fetchReports = useCallback(async () => {
    setLoadingReports(true);
    try {
      const response = await api.get('/reports', {
        params: {
          report_type: 'envis_defect_report',
          limit: 100
        }
      });
      console.log('Fetched reports:', response.data);
      setReports(response.data.data || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setReports([]);
    } finally {
      setLoadingReports(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Fetch defect types from API - only ENViS road-related defect types
  const fetchDefectTypes = useCallback(async () => {
    setLoadingDefectTypes(true);
    try {
      // Fetch only road asset defect types (asset_type_id: 1) for ENViS reports
      const response = await classificationApi.getDefectTypes({
        asset_type_id: 1 // Road asset type - same as AnalyticsPage
      });
      console.log('Fetched ENViS defect types:', response.data);
      setDefectTypes(response.data || []);
    } catch (error) {
      console.error('Error fetching defect types:', error);
      setDefectTypes([]);
    } finally {
      setLoadingDefectTypes(false);
    }
  }, []);

  // Fetch all registered vehicle sources for the authenticated organization.
  const fetchVehicles = useCallback(async () => {
    setLoadingVehicles(true);
    setVehiclesError(null);

    try {
      const pageSize = 1000;
      let offset = 0;
      let total = 0;
      const allVehicles = [];

      do {
        const response = await envisDevicesApi.getAll({ limit: pageSize, offset });
        const batch = response.data?.devices || [];
        total = response.data?.total ?? batch.length;
        allVehicles.push(...batch);

        if (batch.length === 0) break;
        offset += batch.length;
      } while (allVehicles.length < total);

      allVehicles.sort((a, b) => {
        const vehicleCompare = (a.vehicle_id || '').localeCompare(b.vehicle_id || '');
        if (vehicleCompare !== 0) return vehicleCompare;
        return (a.device_name || '').localeCompare(b.device_name || '');
      });

      setVehiclesData(allVehicles);
    } catch (error) {
      console.error('Error fetching vehicle sources:', error);
      setVehiclesData([]);
      setSelectAllVehicles(true);
      setSelectedVehicleUuids([]);
      setVehiclesError('Vehicle sources could not be loaded. Reports can still include all vehicles.');
    } finally {
      setLoadingVehicles(false);
    }
  }, []);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  // Fetch defects count when form parameters change
  useEffect(() => {
    const fetchDefectsCount = async () => {
      // Only fetch if we have the required parameters
      if (!selectedNetwork || !startDate || !endDate || !hasChosenDefectTypes) {
        setValidatedDefectsCount(null);
        return;
      }

      setLoadingDefectsCount(true);
      try {
        // Every filter below is applied server-side, so the endpoint's `total`
        // is the exact match count. Only one row is requested because the count
        // is all this effect needs.
        const queryParams = {
          from_date: startDate.toISOString().split('T')[0],
          to_date: endDate.toISOString().split('T')[0],
          validation_status: findingStatus,
          limit: 1
        };

        // Vehicle filtering is handled server-side to avoid downloading
        // findings from vehicle sources the user did not select.
        if (!selectAllVehicles && selectedVehicleUuids.length > 0) {
          queryParams.device_uuids = selectedVehicleUuids.join(',');
        }

        // The backend combines network_ids and section_ids with OR logic, so only
        // the narrower of the two is sent: specific sections always belong to the
        // selected network.
        if (selectedSections === 'specific' && specificSections.length > 0) {
          queryParams.section_ids = specificSections.join(',');
        } else if (selectedNetwork && selectedNetwork !== 'all') {
          queryParams.network_ids = selectedNetwork;
        }

        if (!selectAllDefects && selectedDefectTypes.length > 0) {
          queryParams.defect_type_ids = selectedDefectTypes.join(',');
        }

        console.log('Fetching findings count with params:', queryParams);

        const response = await api.get('/envis-findings/table-findings', {
          params: queryParams
        });

        const count = response.data?.total ?? 0;
        console.log('Final findings count:', count);
        setValidatedDefectsCount(count);
      } catch (error) {
        console.error('Error fetching findings count:', error);
        setValidatedDefectsCount(0);
      } finally {
        setLoadingDefectsCount(false);
      }
    };

    fetchDefectsCount();
  }, [selectedNetwork, startDate, endDate, hasChosenDefectTypes, selectAllDefects, selectedDefectTypes, selectedSections, specificSections, selectAllVehicles, selectedVehicleUuids, findingStatus]);

  // Fetch stats data on component mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStats(prev => ({ ...prev, loading: true }));
        
                 // Get current month start date for validated defects filter
         const currentDate = new Date();
         const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
         const fromDate = firstDayOfMonth.toISOString().split('T')[0]; // YYYY-MM-DD format
         
         // Fetch data in parallel
         const [
           deviceStatsResponse,
           validatedFindingsResponse,
           networksResponse,
           inspectionsResponse,
           reportsCountResponse
         ] = await Promise.all([
           // ENViS Devices Stats
           api.get('/envis-devices/stats'),
           
           // Validated Defects This Month - using the same endpoint as DefectsPage
           api.get('/envis-findings/table-findings', {
             params: {
               from_date: fromDate,
               validation_status: 'validated', // Only get validated findings
               limit: 1 // We only need the count, not the actual data
             }
           }),
           
           // Networks data and count
           api.get('/networks?limit=1000'), // Get all networks to count them
           
           // Inspections count as proxy for reports generated
           api.get('/inspections?limit=1').catch(() => ({ data: [] })),
           
           // Reports count for this org
           api.get('/reports', {
             params: {
               report_type: 'envis_defect_report',
               limit: 1 // only need total
             }
           })
         ]);

         // Get validated defects count from the table-findings API (same as DefectsPage)
         const validatedDefectsThisMonth = validatedFindingsResponse.data.total || 0;

         const reportsGeneratedCount = reportsCountResponse.data.total || 0;

         // Store networks data for the dialog
         setNetworksData(networksResponse.data || []);

         setStats({
           activeDevices: deviceStatsResponse.data.active_devices || 0,
           aiDetections: validatedDefectsThisMonth,
           networks: networksResponse.data.length || 0,
           reportsGenerated: reportsGeneratedCount,
           loading: false
         });

      } catch (error) {
        console.error('Error fetching stats:', error);
                 // Set fallback values on error
         setNetworksData([]);
         setStats({
           activeDevices: 0,
           aiDetections: 0, // This represents validated defects
           networks: 0,
           reportsGenerated: 0,
           loading: false
         });
      }
    };

    fetchStats();
    fetchDefectTypes();
  }, [fetchDefectTypes]);

  // Fetch sections when network is selected
  const fetchSectionsForNetwork = useCallback(async (networkId) => {
    if (!networkId) {
      setSectionsData([]);
      return;
    }

    setLoadingSections(true);
    try {
      const response = await api.get(`/networks/${networkId}/sections`);
      setSectionsData(response.data || []);
    } catch (error) {
      console.error('Error fetching sections for network:', error);
      setSectionsData([]);
      // Don't show error message as this is not critical
    } finally {
      setLoadingSections(false);
    }
  }, []);

  // Get sections for selected network (now using real data)
  const getAvailableSections = () => {
    return sectionsData;
  };

  // Filter reports based on search
  const filteredReports = reports.filter(report => {
    return report.report_name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Handlers
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFormatChange = (event) => {
    setSelectedFormat(event.target.value);
  };

  const handleCreateNewReport = () => {
    setOpenGenerateDialog(true);
  };

  const handleGenerateClose = () => {
    setOpenGenerateDialog(false);
    // Reset form
    setSelectedNetwork('');
    setSelectedSections('all');
    setSpecificSections([]);
    setSelectedDefectTypes([]);
    setSelectAllDefects(false);
    setHasChosenDefectTypes(false);
    setSelectedFormat('');
    setStartDate(null);
    setEndDate(null);
    setIncludeRepeatedFindings(false);
    setOnlyDefectsWithRepeated(false);
    setSelectAllVehicles(true);
    setSelectedVehicleUuids([]);
    setFindingStatus('validated');
    // Reset defects count
    setValidatedDefectsCount(null);
    setLoadingDefectsCount(false);
  };

  const handleNetworkChange = (event) => {
    const networkId = event.target.value;
    setSelectedNetwork(networkId);
    // Reset sections when network changes
    setSelectedSections('all');
    setSpecificSections([]);
    // Fetch sections for the selected network
    fetchSectionsForNetwork(networkId);
  };

  const handleSectionTypeChange = (event) => {
    setSelectedSections(event.target.value);
    if (event.target.value === 'all') {
      setSpecificSections([]);
    }
  };

  const handleSpecificSectionChange = (sectionId) => {
    setSpecificSections(prev => {
      if (prev.includes(sectionId)) {
        return prev.filter(id => id !== sectionId);
      } else {
        return [...prev, sectionId];
      }
    });
  };

  const handleDefectTypeChange = (defectId) => {
    setHasChosenDefectTypes(true); // User has made a choice
    setSelectedDefectTypes(prev => {
      if (prev.includes(defectId)) {
        return prev.filter(id => id !== defectId);
      } else {
        return [...prev, defectId];
      }
    });
  };

  const handleSelectAllDefectsChange = (event) => {
    setHasChosenDefectTypes(true); // User has made a choice
    setSelectAllDefects(event.target.checked);
    if (event.target.checked) {
      setSelectedDefectTypes([]);
    }
  };

  const getVehicleLabel = (vehicle, includeStatus = false) => {
    if (!vehicle) return 'Unknown vehicle';

    const deviceName = vehicle.device_name || 'Vehicle';
    const vehicleId = vehicle.vehicle_id || vehicle.device_uuid?.substring(0, 8) || 'N/A';
    const label = `${deviceName} (${vehicleId})`;
    return includeStatus && vehicle.is_active === false ? `${label} - Inactive` : label;
  };

  const handleVehicleSelectionChange = (event) => {
    const value = event.target.value;
    const nextValues = typeof value === 'string' ? value.split(',') : value;

    if (nextValues.includes('all')) {
      if (!selectAllVehicles) {
        setSelectAllVehicles(true);
        setSelectedVehicleUuids([]);
        return;
      }

      const specificValues = nextValues.filter(item => item !== 'all');
      if (specificValues.length > 0) {
        setSelectAllVehicles(false);
        setSelectedVehicleUuids(specificValues);
      } else {
        setSelectAllVehicles(true);
        setSelectedVehicleUuids([]);
      }
      return;
    }

    if (nextValues.length === 0) {
      setSelectAllVehicles(true);
      setSelectedVehicleUuids([]);
      return;
    }

    setSelectAllVehicles(false);
    setSelectedVehicleUuids(nextValues);
  };

  const getVehicleScopeSummary = () => {
    if (selectAllVehicles) {
      return vehiclesData.length > 0
        ? `All vehicles (${vehiclesData.length} registered)`
        : 'All vehicles';
    }

    const selectedVehicles = vehiclesData.filter(vehicle =>
      selectedVehicleUuids.includes(vehicle.device_uuid)
    );

    if (selectedVehicles.length === 1) {
      return getVehicleLabel(selectedVehicles[0]);
    }

    return `${selectedVehicleUuids.length} vehicles selected`;
  };

  // Updated validation logic to require explicit user choices for all steps
  const canGenerate = selectedNetwork && startDate && endDate && 
    endDate > startDate && // Ensure end date is after start date
    hasChosenDefectTypes && // User must explicitly choose defect types
    (selectAllDefects || selectedDefectTypes.length > 0) && // Must have defect types selected
    (selectAllVehicles || selectedVehicleUuids.length > 0) &&
    selectedFormat && // Must select output format
    (
      // If network has no sections, automatically valid
      (!loadingSections && getAvailableSections().length === 0) ||
      // If network has sections, check selection
      (selectedSections === 'all' || specificSections.length > 0)
    );

  const handlePreviewClick = () => {
    setOpenPreviewModal(true);
  };

  const handlePreviewClose = () => {
    setOpenPreviewModal(false);
    setPreviewFilters(null); // Reset on close
  };

  const handleLayoutPreviewClick = () => {
    setPreviewFilters({
      networkId: 'N/A',
      sectionsMode: 'all',
      sectionIds: [],
      startDate: new Date(),
      endDate: new Date(),
      selectAllDefects: true,
      defectTypeIds: [],
      selectAllVehicles: true,
      deviceUuids: [],
      validationStatus: 'validated',
      includeRepeatedFindings: false,
      onlyDefectsWithRepeated: false,
      isEmpty: true,
    });
    setOpenPreviewModal(true);
  };

  // View report in new tab
  const handleViewReport = async (report) => {
    try {
      // The backend now returns a JSON object with a presigned URL
      const response = await api.get(`/reports/${report.id}/download`);
      if (response.data.success && response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        throw new Error(response.data.error || 'Failed to get download link.');
      }
    } catch (err) {
      console.error('Error viewing report:', err);
      // You might want to add a user-facing notification here
    }
  };

  // Download report to disk
  const handleDownloadReport = async (report) => {
    try {
      // The backend now returns a JSON object with a presigned URL
      const response = await api.get(`/reports/${report.id}/download`);
      if (response.data.success && response.data.url) {
        // The browser will handle the download directly from the S3 URL.
        // To suggest a filename, we create a temporary link.
        const link = document.createElement('a');
        link.href = response.data.url;
        // The 'download' attribute suggests a filename to the browser.
        // Note: This might not work for all browser/server configurations due to security policies,
        // but it's the standard approach. The file will still download.
        link.setAttribute('download', response.data.fileName || `${report.report_name}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error(response.data.error || 'Failed to get download link.');
      }
    } catch (err) {
      console.error('Error downloading report:', err);
      // You might want to add a user-facing notification here
    }
  };

  return (
    <DisciplineAccessControl requiredDiscipline="Road Pavement">
    <Box sx={{ padding: 3, maxWidth: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Reports
        </Typography>
          <Typography variant="body1" color="text.secondary">
            AI-powered defect detection reports from IRIS devices
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search reports..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              width: { xs: '100%', sm: '240px' },
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNewReport}
            sx={{
              borderRadius: '8px',
              px: 3,
              py: 1.2,
              backgroundColor: theme.palette.primary.main,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.12)',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.16)',
              }
            }}
          >
            Create New Report
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            borderRadius: 2, 
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.light, 0.02)})`
          }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 48, height: 48 }}>
                <MemoryIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  {stats.loading ? <CircularProgress size={20} /> : stats.activeDevices.toLocaleString()}
        </Typography>
                <Typography variant="body2" color="text.secondary">Active IRIS Devices</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            borderRadius: 2, 
            border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
            background: `linear-gradient(45deg, ${alpha(theme.palette.success.main, 0.05)}, ${alpha(theme.palette.success.light, 0.02)})`
          }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.success.main, width: 48, height: 48 }}>
                <SmartToyIcon />
              </Avatar>
                    <Box>
                <Typography variant="h5" fontWeight={600}>
                  {stats.loading ? <CircularProgress size={20} /> : stats.aiDetections.toLocaleString()}
                      </Typography>
                <Typography variant="body2" color="text.secondary">Validated Defects This Month</Typography>
                    </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            borderRadius: 2, 
            border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
            background: `linear-gradient(45deg, ${alpha(theme.palette.warning.main, 0.05)}, ${alpha(theme.palette.warning.light, 0.02)})`
          }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.warning.main, width: 48, height: 48 }}>
                <NetworkCheckIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  {stats.loading ? <CircularProgress size={20} /> : stats.networks.toLocaleString()}
                    </Typography>
                <Typography variant="body2" color="text.secondary">Highway Networks</Typography>
                    </Box>
            </CardContent>
          </Card>
              </Grid>
        
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            borderRadius: 2, 
            border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
            background: `linear-gradient(45deg, ${alpha(theme.palette.info.main, 0.05)}, ${alpha(theme.palette.info.light, 0.02)})`
          }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.info.main, width: 48, height: 48 }}>
                <DescriptionIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  {stats.loading ? <CircularProgress size={20} /> : stats.reportsGenerated.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">Reports Generated</Typography>
              </Box>
            </CardContent>
          </Card>
            </Grid>
        </Grid>

      {/* Recent Reports Section */}
      <Paper sx={{ p: 3, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            Reports
          </Typography>
        </Box>
        
        <TableContainer sx={{ borderRadius: '10px', boxShadow: 'none', border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>Report Name</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>Network</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>Generated</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>Format</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>Generated By</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingReports ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 4, textAlign: 'center' }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredReports.map((report) => {
                // Parse parameters JSON (may be null)
                // The `parameters` field is now a JSON object, not a string.
                // No need to parse it.
                const parsedParams = report.parameters || {};

                // Network name lookup
                const networkId = parsedParams.filters?.networkId;
                let networkName = 'All Networks';
                if (networkId && networksData.length > 0) {
                  const net = networksData.find(n => n.id === parseInt(networkId));
                  if (net) networkName = net.name;
                }

                const defectsFound = parsedParams.defectCount ?? 'N/A';

                return (
                  <TableRow 
                    key={report.id}
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      },
                      '& .MuiTableCell-root': {
                        py: 1.5,
                        borderColor: alpha(theme.palette.divider, 0.08)
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PictureAsPdfIcon sx={{ mr: 1.5, color: theme.palette.error.main }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {report.report_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                      label={networkName} 
                        size="small" 
                        sx={{ 
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          borderRadius: '6px',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {new Date(report.created_at).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(report.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={report.report_type === 'envis_defect_report' ? 'PDF' : report.report_type}
                        size="small" 
                        sx={{ 
                          backgroundColor: alpha(theme.palette.grey[500], 0.1),
                          color: theme.palette.text.primary,
                          fontSize: '0.75rem',
                          borderRadius: '6px',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{report.generated_by_name || 'Unknown'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Tooltip title="View Report">
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewReport(report)}
                            sx={{ 
                              color: theme.palette.primary.main,
                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                              }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download PDF">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDownloadReport(report)}
                            sx={{ 
                              color: theme.palette.success.main,
                              backgroundColor: alpha(theme.palette.success.main, 0.08),
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.success.main, 0.15),
                              }
                            }}
                          >
                            <FileDownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Share">
                          <IconButton 
                            size="small" 
                            sx={{ 
                              color: theme.palette.info.main,
                              backgroundColor: alpha(theme.palette.info.main, 0.08),
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.info.main, 0.15),
                              }
                            }}
                          >
                            <ShareIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              
              {!loadingReports && filteredReports.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 4, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <MemoryIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                    <Typography color="text.secondary">
                        No IRIS reports found. Create your first report to get started!
                    </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ENViS Report Generation Dialog */}
      <Dialog 
        open={openGenerateDialog} 
        onClose={handleGenerateClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: theme.shadows[24],
            maxWidth: 900,
            height: '85vh'
          }
        }}
      >
        {/* Dialog Header */}
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          pb: 2,
          pt: 3,
          px: 4
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }}>
              <MemoryIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                Generate Defect Report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create comprehensive AI-powered defect analysis reports
                </Typography>
              </Box>
          </Box>
          <IconButton onClick={handleGenerateClose} size="large">
            <CloseIcon />
              </IconButton>
            </DialogTitle>
        
        {/* Dialog Content */}
        <DialogContent sx={{ p: 4, overflow: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

            {/* Report content: validated defects (default) or unreviewed findings */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FactCheckIcon color="primary" />
                Report Content
              </Typography>

              <Box sx={{
                p: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.02)
              }}>
                <RadioGroup
                  value={findingStatus}
                  onChange={(event) => setFindingStatus(event.target.value)}
                  sx={{ gap: 1 }}
                >
                  {REPORT_CONTENT_OPTIONS.map(option => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            {option.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        borderRadius: 2,
                        p: 2,
                        m: 0,
                        width: '100%',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.04)
                        }
                      }}
                    />
                  ))}
                </RadioGroup>

                {findingStatus === 'draft' && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    These findings have not been reviewed. Defect types, severity and locations
                    are AI predictions and may be wrong or duplicated. Pending findings are also
                    far more numerous than validated defects, so narrow the date range, sections
                    or vehicles before generating.
                  </Alert>
                )}
              </Box>
            </Box>

            {/* Step 1: Network Selection */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 24, 
                  height: 24, 
                  borderRadius: '50%', 
                  bgcolor: theme.palette.primary.main, 
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}>
                  1
                </Box>
                Select Highway Network
              </Typography>
              
              <FormControl fullWidth>
                <InputLabel>Highway Network *</InputLabel>
                <Select
                  value={selectedNetwork}
                  label="Highway Network *"
                  onChange={handleNetworkChange}
                  disabled={loadingNetworks || networksData.length === 0}
                  size="medium"
                  sx={{ 
                    '& .MuiSelect-select': { 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: 1
                    }
                  }}
                >
                  {loadingNetworks ? (
                    <MenuItem disabled>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={20} />
                        <Typography>Loading networks...</Typography>
                      </Box>
                    </MenuItem>
                  ) : networksData.length === 0 ? (
                    <MenuItem disabled>
                      <Typography color="text.secondary">
                        No networks available
                      </Typography>
                    </MenuItem>
                  ) : (
                    networksData.map((network) => (
                      <MenuItem key={network.id} value={network.id}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <NetworkCheckIcon color="primary" />
                            <Box>
                              <Typography variant="body1" fontWeight={500}>
                                {network.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Code: {network.code}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip 
                            label={`${network.section_count || 0} sections`} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>

            {/* Step 2: Section Selection */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 24, 
                  height: 24, 
                  borderRadius: '50%', 
                  bgcolor: selectedNetwork ? theme.palette.primary.main : theme.palette.grey[400], 
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}>
                  2
                </Box>
                Choose Road Sections
              </Typography>
              
              <Box sx={{ 
                opacity: selectedNetwork ? 1 : 0.5,
                pointerEvents: selectedNetwork ? 'auto' : 'none'
              }}>
                {/* Show loading state */}
                {selectedNetwork && loadingSections && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    py: 4, 
                    justifyContent: 'center',
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.02)
                  }}>
                    <CircularProgress size={24} />
                    <Typography color="text.secondary">
                      Loading sections for {networksData.find(n => n.id === parseInt(selectedNetwork))?.name}...
                    </Typography>
                  </Box>
                )}

                {/* Show no sections message - auto use whole network */}
                {selectedNetwork && !loadingSections && getAvailableSections().length === 0 && (
                  <Box sx={{ 
                    p: 3,
                    border: `2px solid ${theme.palette.success.main}`,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.05),
                    textAlign: 'center'
                  }}>
                    <NetworkCheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                    <Typography variant="h6" color="success.main" fontWeight={600} gutterBottom>
                      Whole Network Selected
                    </Typography>
                    <Typography variant="body1" color="text.primary" gutterBottom>
                      {networksData.find(n => n.id === parseInt(selectedNetwork))?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This network has no defined sections. The report will include all defects from the entire network.
                    </Typography>
                  </Box>
                )}

                {/* Show section selection options only if sections exist */}
                {selectedNetwork && !loadingSections && getAvailableSections().length > 0 && (
                  <RadioGroup
                    value={selectedSections}
                    onChange={handleSectionTypeChange}
                    sx={{ gap: 1 }}
                  >
                    <FormControlLabel 
                      value="all" 
                      control={<Radio />} 
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            All sections in selected network
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Include all {sectionsData.length} sections
                          </Typography>
                        </Box>
                      }
                      sx={{ 
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        borderRadius: 2,
                        p: 2,
                        m: 0,
                        width: '100%',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.04)
                        }
                      }}
                    />
                    <FormControlLabel 
                      value="specific" 
                      control={<Radio />} 
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            Select specific sections
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Choose individual sections for targeted reporting
                          </Typography>
                        </Box>
                      }
                      sx={{ 
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        borderRadius: 2,
                        p: 2,
                        m: 0,
                        width: '100%',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.04)
                        }
                      }}
                    />
                  </RadioGroup>
                )}
                
                {selectedSections === 'specific' && selectedNetwork && (
                  <Box sx={{ 
                    mt: 3, 
                    p: 3, 
                    border: `2px solid ${theme.palette.primary.main}`, 
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.02)
                  }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                      Select Sections:
                    </Typography>
                    
                    {loadingSections ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
                        <CircularProgress size={24} />
                        <Typography color="text.secondary">
                          Loading sections for {networksData.find(n => n.id === parseInt(selectedNetwork))?.name}...
                        </Typography>
                      </Box>
                    ) : getAvailableSections().length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <DirectionsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary">
                          No sections found for this network
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ 
                        maxHeight: 300, 
                        overflow: 'auto',
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        borderRadius: 2,
                        bgcolor: 'background.paper'
                      }}>
                        {getAvailableSections().map((section, index) => (
                          <Box 
                            key={section.id}
                            sx={{ 
                              borderBottom: index < getAvailableSections().length - 1 
                                ? `1px solid ${alpha(theme.palette.divider, 0.1)}` 
                                : 'none'
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={specificSections.includes(section.id)}
                                  onChange={() => handleSpecificSectionChange(section.id)}
                                />
                              }
                              label={
                                <Box sx={{ py: 1 }}>
                                  <Typography variant="body1" fontWeight={500}>
                                    {section.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {section.code} • KM {section.start_km || 0} - {section.end_km || 0}
                                  </Typography>
                                </Box>
                              }
                              sx={{ 
                                width: '100%',
                                px: 2,
                                py: 1,
                                m: 0,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.04)
                                }
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                    
                    {getAvailableSections().length > 0 && (
                      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                        <Button 
                          size="small" 
                          variant="outlined"
                          onClick={() => {
                            const allSectionIds = getAvailableSections().map(s => s.id);
                            setSpecificSections(allSectionIds);
                          }}
                        >
                          Select All
                        </Button>
                        <Button 
                          size="small" 
                          variant="outlined"
                          onClick={() => setSpecificSections([])}
                        >
                          Clear All
                        </Button>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
                          {specificSections.length} of {getAvailableSections().length} selected
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Step 3: Vehicle Selection */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}>
                  3
                </Box>
                Select Vehicles
              </Typography>

              <Box sx={{
                p: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.02)
              }}>
                {vehiclesError && (
                  <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                      <Button color="inherit" size="small" onClick={fetchVehicles}>
                        Retry
                      </Button>
                    }
                  >
                    {vehiclesError}
                  </Alert>
                )}

                <FormControl fullWidth>
                  <InputLabel>Vehicles</InputLabel>
                  <Select
                    multiple
                    value={selectAllVehicles ? ['all'] : selectedVehicleUuids}
                    label="Vehicles"
                    onChange={handleVehicleSelectionChange}
                    disabled={loadingVehicles}
                    renderValue={(selected) => {
                      if (selected.includes('all') || selected.length === 0) {
                        return vehiclesData.length > 0
                          ? `All Vehicles (${vehiclesData.length})`
                          : 'All Vehicles';
                      }

                      if (selected.length === 1) {
                        const vehicle = vehiclesData.find(item => item.device_uuid === selected[0]);
                        return getVehicleLabel(vehicle);
                      }

                      return `${selected.length} vehicles selected`;
                    }}
                  >
                    <MenuItem value="all">
                      <Checkbox checked={selectAllVehicles} />
                      <ListItemText
                        primary={`All Vehicles${vehiclesData.length > 0 ? ` (${vehiclesData.length})` : ''}`}
                        secondary="Include findings from every registered vehicle source"
                      />
                    </MenuItem>

                    {vehiclesData.length === 0 && !loadingVehicles && (
                      <MenuItem disabled>
                        <ListItemText primary="No registered vehicles found" />
                      </MenuItem>
                    )}

                    {vehiclesData.map(vehicle => (
                      <MenuItem key={vehicle.device_uuid} value={vehicle.device_uuid}>
                        <Checkbox checked={selectedVehicleUuids.includes(vehicle.device_uuid)} />
                        <ListItemText
                          primary={getVehicleLabel(vehicle)}
                          secondary={vehicle.is_active === false ? 'Inactive - available for historical reports' : 'Active'}
                        />
                      </MenuItem>
                    ))}
                  </Select>

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Vehicles are organization-wide and are not limited by the selected network.
                  </Typography>
                </FormControl>

                {loadingVehicles && (
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Loading registered vehicles...
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Step 4: Date Range */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 24, 
                  height: 24, 
                  borderRadius: '50%', 
                  bgcolor: theme.palette.primary.main, 
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}>
                  4
                </Box>
                Set Report Date Range
              </Typography>
              
              <Box sx={{ 
                p: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.02)
              }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                      <DateTimePicker
                        label="Start Date & Time *"
                      value={startDate}
                        onChange={(newValue) => {
                          setStartDate(newValue);
                          // Auto-adjust end date if it becomes invalid
                          if (endDate && newValue && endDate <= newValue) {
                            // Set end date to 1 hour after start date
                            const newEndDate = new Date(newValue);
                            newEndDate.setHours(newEndDate.getHours() + 1);
                            setEndDate(newEndDate);
                          }
                        }}
                        format="dd/MM/yyyy HH:mm"
                        maxDate={new Date()} // Can't select future dates
                      slotProps={{ 
                        textField: { 
                          fullWidth: true, 
                            size: 'medium',
                            helperText: 'Select the starting date and time for the report'
                        } 
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <DateTimePicker
                        label="End Date & Time *"
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue)}
                        format="dd/MM/yyyy HH:mm"
                        minDate={startDate} // Can't be earlier than start date
                        maxDate={new Date()} // Can't select future dates
                        disabled={!startDate} // Must select start date first
                      slotProps={{ 
                        textField: { 
                          fullWidth: true, 
                            size: 'medium',
                            helperText: startDate 
                              ? 'Select the ending date and time for the report'
                              : 'Please select a start date first',
                            error: endDate && startDate && endDate <= startDate,
                        } 
                      }}
                    />
                      {endDate && startDate && endDate <= startDate && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                          End date must be after start date
                        </Typography>
                      )}
                  </Grid>
                </Grid>
                  
                  {/* Date Range Summary */}
                  {startDate && endDate && endDate > startDate && (
                    <Box sx={{ 
                      mt: 3, 
                      p: 2, 
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      borderRadius: 1,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                    }}>
                      <Typography variant="subtitle2" color="success.main" fontWeight={600} gutterBottom>
                        📅 Report Date Range Summary
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2">
                          <strong>From:</strong> {startDate.toLocaleDateString('en-GB')} at {startDate.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                        <Typography variant="body2">
                          <strong>To:</strong> {endDate.toLocaleDateString('en-GB')} at {endDate.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Duration: {Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))} day(s), {Math.ceil((endDate - startDate) / (1000 * 60 * 60))} hour(s)
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {/* Quick Date Range Presets */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Quick Presets:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const now = new Date();
                          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                          setStartDate(today);
                          setEndDate(now);
                        }}
                      >
                        Today
              </Button>
              <Button 
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const now = new Date();
                          const yesterday = new Date(now);
                          yesterday.setDate(yesterday.getDate() - 1);
                          const yesterdayStart = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
                          const yesterdayEnd = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59);
                          setStartDate(yesterdayStart);
                          setEndDate(yesterdayEnd);
                        }}
                      >
                        Yesterday
              </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const now = new Date();
                          const lastWeek = new Date(now);
                          lastWeek.setDate(lastWeek.getDate() - 7);
                          setStartDate(lastWeek);
                          setEndDate(now);
                        }}
                      >
                        Last 7 Days
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const now = new Date();
                          const lastMonth = new Date(now);
                          lastMonth.setDate(lastMonth.getDate() - 30);
                          setStartDate(lastMonth);
                          setEndDate(now);
                        }}
                      >
                        Last 30 Days
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const now = new Date();
                          const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                          setStartDate(firstDayOfMonth);
                          setEndDate(now);
                        }}
                      >
                        This Month
                      </Button>
                    </Box>
                  </Box>
                </LocalizationProvider>
              </Box>
            </Box>

            {/* Step 5: Defect Types */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 24, 
                  height: 24, 
                  borderRadius: '50%', 
                  bgcolor: theme.palette.primary.main, 
                  color: 'white', 
              display: 'flex', 
              alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}>
                  5
                </Box>
                Choose Defect Types
                {loadingDefectTypes && (
                  <CircularProgress size={16} sx={{ ml: 1 }} />
                )}
                {!loadingDefectTypes && !hasChosenDefectTypes && (
                  <Chip 
                    label="Required" 
                    size="small" 
                    color="warning" 
                    variant="outlined"
                    sx={{ ml: 1, fontSize: '0.7rem' }}
                  />
                )}
              </Typography>
              
              <Box sx={{ 
                p: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.02)
              }}>
                {loadingDefectTypes ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      Loading defect types...
                </Typography>
              </Box>
                ) : defectTypes.length === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    py: 4,
                    color: 'text.secondary'
                  }}>
                    <WarningIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" gutterBottom>
                      No Defect Types Available
                </Typography>
                    <Typography variant="body2" textAlign="center">
                      No defect types are configured for your organization. 
                      Please contact your administrator to set up defect types.
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={fetchDefectTypes}
                      sx={{ mt: 2 }}
                      startIcon={<RefreshIcon />}
                    >
                      Retry
                    </Button>
              </Box>
                ) : (
                  <Box>
                    {/* Select All Option */}
                    <Box sx={{ 
                      p: 2, 
                      mb: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      borderRadius: 1,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                    }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectAllDefects}
                            onChange={handleSelectAllDefectsChange}
                            indeterminate={!selectAllDefects && selectedDefectTypes.length > 0 && selectedDefectTypes.length < defectTypes.length}
                          />
                        }
                        label={
              <Box>
                            <Typography variant="body1" fontWeight={600}>
                              Include all defect types ({defectTypes.length})
                            </Typography>
                <Typography variant="caption" color="text.secondary">
                              Select all available defect types for comprehensive reporting
                </Typography>
              </Box>
                        }
                      />
                    </Box>
                    
                    {/* Individual Defect Type Selection */}
                    {!selectAllDefects && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                          Select specific defect types:
                        </Typography>
                        <Grid container spacing={2}>
                          {defectTypes.map((defectType) => {
                            // Generate a color for defect type if not provided
                            const colors = ['#f44336', '#ff9800', '#ffeb3b', '#4caf50', '#2196f3', '#9c27b0', '#607d8b', '#795548'];
                            const defectColor = defectType.color || colors[defectType.id % colors.length];
                            
                            return (
                              <Grid item xs={12} sm={6} key={defectType.id}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={selectedDefectTypes.includes(defectType.id)}
                                      onChange={() => handleDefectTypeChange(defectType.id)}
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                      <Box
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          borderRadius: '50%',
                                          backgroundColor: defectColor
                                        }}
                                      />
                                      <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="body2" fontWeight={500}>
                                          {defectType.name}
                                        </Typography>
                                        {defectType.description && (
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            {defectType.description}
                                          </Typography>
                                        )}
                                      </Box>
                                      {defectType.code && (
                                        <Chip 
                                          label={defectType.code} 
                                          size="small" 
                  variant="outlined" 
                                          sx={{ fontSize: '0.7rem', height: 20 }}
                                        />
                                      )}
                                    </Box>
                                  }
                                  sx={{ 
                                    width: '100%',
                                    m: 0,
                                    p: 1.5,
                                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                    borderRadius: 1,
                                    bgcolor: selectedDefectTypes.includes(defectType.id) 
                                      ? alpha(theme.palette.primary.main, 0.08)
                                      : 'transparent',
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                                      borderColor: alpha(theme.palette.primary.main, 0.3)
                                    }
                                  }}
                                />
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Optional: Include Repeated Findings */}
            <Box sx={{
              p: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.03)
            }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeRepeatedFindings}
                    onChange={(e) => {
                      setIncludeRepeatedFindings(e.target.checked);
                      if (!e.target.checked) setOnlyDefectsWithRepeated(false);
                    }}
                    color="info"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Include Repeated Findings
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      For each defect, include additional pages showing repeated detections from different days
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', m: 0 }}
              />
              {includeRepeatedFindings && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={onlyDefectsWithRepeated}
                      onChange={(e) => setOnlyDefectsWithRepeated(e.target.checked)}
                      color="info"
                      size="small"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Only Defects with Repeated Findings
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Exclude defects that have no repeated detections from the report
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', m: 0, ml: 4, mt: 1 }}
                />
              )}
            </Box>

            {/* Step 6: Output Format */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 24, 
                  height: 24, 
                  borderRadius: '50%', 
                  bgcolor: theme.palette.primary.main, 
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}>
                  6
                </Box>
                Select Output Format
                {!selectedFormat && (
                  <Chip 
                    label="Required" 
                    size="small" 
                    color="warning" 
                    variant="outlined"
                    sx={{ ml: 1, fontSize: '0.7rem' }}
                  />
                )}
              </Typography>
              
              <Box sx={{ 
                p: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.02)
              }}>
                <FormControl fullWidth>
                  <InputLabel>Output Format *</InputLabel>
                  <Select
                    value={selectedFormat}
                    label="Output Format *"
                    onChange={handleFormatChange}
                    size="medium"
                    sx={{
                      bgcolor: 'white',
                      borderRadius: '8px'
                    }}
                  >
                    {formatOptions.map((format) => (
                      <MenuItem 
                        key={format} 
                        value={format}
                        disabled={format === 'XLSX' || format === 'CSV'}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {format === 'PDF' && <PictureAsPdfIcon color="error" />}
                          {format !== 'PDF' && <InsertDriveFileIcon color={format === 'XLSX' || format === 'CSV' ? "disabled" : "primary"} />}
                          <Typography 
                            variant="body1" 
                            color={format === 'XLSX' || format === 'CSV' ? "text.disabled" : "text.primary"}
                          >
                            {format}
                            {(format === 'XLSX' || format === 'CSV') && " (Coming Soon)"}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {!selectedFormat && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Choose the format for your IRIS defect report download
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Box>
              </Box>
            </DialogContent>
        
        {/* Summary Box */}
        {canGenerate && (
          <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
            <Typography variant="body2" color="primary" fontWeight={500} gutterBottom>
              Report Summary:
            </Typography>
            <List dense>
              <ListItem sx={{ py: 0.25, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <FactCheckIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={getReportContentOption(findingStatus).label}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.25, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <NetworkCheckIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={networksData.find(n => n.id === parseInt(selectedNetwork))?.name}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.25, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <DirectionsIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary={
                    // Handle networks with no sections
                    getAvailableSections().length === 0 
                      ? "Whole network (no sections defined)"
                      : selectedSections === 'all' 
                        ? `All sections (${getAvailableSections().length})`
                        : `${specificSections.length} specific sections`
                  }
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.25, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <DirectionsCarIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={getVehicleScopeSummary()}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.25, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CalendarTodayIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary={`${startDate?.toLocaleDateString()} - ${endDate?.toLocaleDateString()}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.25, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <BugReportIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary={selectAllDefects 
                    ? `All defect types (${defectTypes.length})`
                    : `${selectedDefectTypes.length} selected defect types`
                  }
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.25, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <SmartToyIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    loadingDefectsCount || validatedDefectsCount === null
                      ? `Counting ${getReportContentOption(findingStatus).noun}...`
                      : `${validatedDefectsCount.toLocaleString()} ${getReportContentOption(findingStatus).noun} found`
                  }
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </List>

            {!loadingDefectsCount && validatedDefectsCount > FINDINGS_FETCH_LIMIT && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Only the first {FINDINGS_FETCH_LIMIT.toLocaleString()} records
                (oldest first) will be included in the report. Narrow the date range,
                sections, vehicles or defect types to cover everything.
              </Alert>
            )}
          </Box>
        )}
        
        <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Note:</strong> Report generation may take several minutes depending on the date range and number of defects. 
            You'll receive a notification when the report is ready for download.
                </Typography>
              </Box>
        
            <DialogActions sx={{
          p: 3,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              justifyContent: 'space-between'
            }}>
          <Box>
            <Button onClick={handleGenerateClose} color="inherit" sx={{ fontWeight: 500 }}>
              Cancel
            </Button>
            <Button
              onClick={handleLayoutPreviewClick}
              sx={{ ml: 1 }}
              variant="outlined"
            >
              Preview Layout
            </Button>
          </Box>
          <Button
            variant="contained"
            startIcon={<VisibilityIcon />}
            disabled={!canGenerate}
            sx={{ fontWeight: 600 }}
            onClick={handlePreviewClick}
          >
            Preview Report
          </Button>
            </DialogActions>
      </Dialog>

      <ReportPreviewModal
        open={openPreviewModal}
        onClose={handlePreviewClose}
        filters={previewFilters || {
          networkId: selectedNetwork,
          sectionsMode: selectedSections,
          sectionIds: specificSections,
          startDate,
          endDate,
          selectAllDefects,
          defectTypeIds: selectedDefectTypes,
          selectAllVehicles,
          deviceUuids: selectedVehicleUuids,
          validationStatus: findingStatus,
          includeRepeatedFindings,
          onlyDefectsWithRepeated
        }}
        availableVehicles={vehiclesData}
        onReportSaved={() => {
          fetchReports();
          handleGenerateClose(); // Close generation dialog on save
        }}
      />
    </Box>
    </DisciplineAccessControl>
  );
};

export default ReportsPage;
